import clsx from 'clsx'
import React, { MouseEvent, VFC } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDate } from 'src/utils/date'
import CardContainer from '@components/CardContainer'
import Flag from '@components/Flag'
import useStyles from './PractitionerCard.styles'
import { ReactComponent as DefaultPractitionerImage } from 'src/images/practitioner-default.svg'
import { useLanguage } from '@hooks/useLanguage'
import { DisplayRole, PractitionerPosition, PractitionerRating } from '@customTypes/Practitioner'
import { ReactComponent as StarIcon } from 'src/images/icon-star.svg'
import { Gender } from '@customTypes/Generic'
import { useGetPractitionerRoles } from './helper'
import { useDateFnsLocale } from '@hooks/useDateFnsLocale'
import { TEST_IDS } from 'src/constants/testIds'
import { getImageUrlWithTransformations } from 'src/utils/getImageUrl'
import { Resize } from '@dentalux/ui-library-core'
import { PillSpecialisations } from '@dentalux/ui-library-core/custom/components/pill-specialisations/pill-specialisations'

export type PractitionerCardProps = {
  imageUrl?: string | null
  name: string
  rating?: PractitionerRating
  positions?: PractitionerPosition[]
  gender?: Gender
  languages?: string[]
  nextAvailable?: string
  isSelected?: boolean
  isDesktop?: boolean
  onClick?: (event: MouseEvent<HTMLElement>) => void
  noRadius?: boolean
  displayRole?: DisplayRole
  specialisations: string[]
}

const PractitionerCard: VFC<PractitionerCardProps> = ({
  imageUrl,
  name,
  languages,
  rating,
  positions,
  gender,
  nextAvailable,
  isSelected,
  isDesktop,
  onClick,
  noRadius,
  displayRole,
  specialisations,
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const language = useLanguage()
  const locale = useDateFnsLocale()
  const rolesString = useGetPractitionerRoles(positions, gender, displayRole, language)

  return (
    <CardContainer
      isSelected={isSelected}
      onClick={onClick}
      isDesktop={isDesktop}
      noRadius={noRadius}
      className={classes.cardContainer}
    >
      <div className={clsx(classes.wrapper, { [classes.selected]: isSelected })}>
        <div
          className={clsx(classes.mainContent, { [classes.desktop]: isDesktop })}
          data-test={TEST_IDS.practitionerCard}
        >
          {imageUrl ? (
            <div
              className={clsx(classes.avatar, { [classes.desktop]: isDesktop, [classes.selected]: isSelected })}
              style={{
                backgroundImage: `url(${getImageUrlWithTransformations({
                  src: imageUrl,
                  quality: 'lightest',
                  transformations: [new Resize(200, 200)],
                })})`,
              }}
            />
          ) : (
            <div className={clsx(classes.avatar, { [classes.desktop]: isDesktop, [classes.selected]: isSelected })}>
              <DefaultPractitionerImage
                className={clsx(classes.defaultPractitionerImage, { [classes.desktop]: isDesktop })}
              />
            </div>
          )}
          <div className={clsx(classes.textCont, { [classes.desktop]: isDesktop })}>
            <div className={clsx(classes.name, { [classes.desktop]: isDesktop })}>{name}</div>
            <div className={clsx(classes.roles)}> {rolesString} </div>
            {nextAvailable && (
              <div className={clsx(classes.nextAvailableDateCont, { [classes.desktop]: isDesktop })}>
                <div className={classes.nextAvailableDateLabel}>{`${t('next_booking')}: `} </div>
                <div className={classes.nextAvailableDate}>{formatDate(nextAvailable, language, locale)}</div>
              </div>
            )}
            <div className={classes.pillSpecialisations}>
              <PillSpecialisations items={specialisations} t={t} sx={{ height: '28px' }} />
            </div>
          </div>
        </div>
        <div className={clsx(classes.bottomContent)}>
          <div className={clsx(classes.languagesWrapper)}>
            {languages &&
              languages.map((lang) => (
                <div className={clsx(classes.flagStyles)} key={lang}>
                  <Flag key={lang} language={lang} isSmall={!isDesktop} />
                </div>
              ))}
          </div>
          {rating?.averageScore && rating.reviewsCount && (
            <div className={clsx(classes.ratingWrapper)}>
              <div>
                <StarIcon />
              </div>
              <div className={clsx(classes.ratingValue)}>{rating?.averageScore}</div>
              <div className={clsx(classes.ratingCount)}>{`(${rating?.reviewsCount})`}</div>
            </div>
          )}
        </div>
      </div>
    </CardContainer>
  )
}

export default PractitionerCard
